import { useState } from "react";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";

// ─── helpers ───────────────────────────────────────────────────────────────────
const fmt      = (n)   => (n != null ? n : "");
const fmtCurr  = (n)   => (n != null ? n : 0);
const fmtDate  = (d)   => (d ? new Date(d).toLocaleDateString("en-IN") : "");
const fmtList  = (arr) => (Array.isArray(arr) ? arr.join(", ") : arr || "");
const safe     = (v)   => v || "";

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// ✅ GST is always calculated at this flat rate — never read from backend.
const DEFAULT_BATTERY_GST = 5;
const DEFAULT_CHARGER_GST = 5;
const DEFAULT_BIKE_GST    = 5;

// ✅ GST-exclusive breakdown — amount is treated as GST-inclusive,
// base is derived by dividing out the rate first, then split into CGST+SGST.
const gstBreakdown = (amount, rate) => {
  const amt = fmtCurr(amount);
  const r = rate || 0;
  const taxable = r > 0 ? round2(amt / (1 + r / 100)) : amt;
  const cgst = round2((taxable * (r / 2)) / 100);
  const sgst = round2((taxable * (r / 2)) / 100);
  const total = round2(taxable + cgst + sgst);
  return { taxable, rate: r, cgst, sgst, total };
};

// ✅ per-bike component amounts + rates
const batteryTotalAmount = (b) =>
  Array.isArray(b.battery) ? b.battery.reduce((s, x) => s + fmtCurr(x.batteryAmount), 0) : 0;

// ✅ CHANGED: always returns the flat default — no backend lookup.
const batteryGstRate = (b) => DEFAULT_BATTERY_GST;

const chargerAmount = (b) => (b.charger ? fmtCurr(b.charger.chargerAmount) : 0);

// ✅ CHANGED: always returns the flat default — no backend lookup.
const chargerGstRate = (b) => DEFAULT_CHARGER_GST;

// ✅ CHANGED: always returns the flat default — no backend lookup.
const bikeGstRate = (b) => DEFAULT_BIKE_GST;

// ✅ the 3 GST breakdowns per bike row
const batteryGst = (b) => gstBreakdown(batteryTotalAmount(b), batteryGstRate(b));
const chargerGst = (b) => gstBreakdown(chargerAmount(b), chargerGstRate(b));
// Bike-only amount = total sell price minus battery+charger amounts bundled into it
const bikeOnlyAmount = (b) =>
  Math.max(fmtCurr(b.salePrice) - batteryTotalAmount(b) - chargerAmount(b), 0);
const bikeGst = (b) => gstBreakdown(bikeOnlyAmount(b), bikeGstRate(b));

const totalGstPayable = (b) =>
  round2(
    bikeGst(b).cgst + bikeGst(b).sgst +
    batteryGst(b).cgst + batteryGst(b).sgst +
    chargerGst(b).cgst + chargerGst(b).sgst
  );

const fmtBatteryList = (batteries) => {
  if (!Array.isArray(batteries) || !batteries.length) return "";
  return batteries
    .map((bt) => {
      const stock = bt.batteryStock || {};
      const label = safe(stock.model) || "Battery";
      const sn = stock.serialNo ? ` (SN:${stock.serialNo})` : "";
      return `${label}${sn} - ₹${fmtCurr(bt.batteryAmount)}`;
    })
    .join("; ");
};

const fmtCharger = (charger) => {
  if (!charger) return "";
  const stock = charger.chargerStock || {};
  const label = safe(stock.model) || "Charger";
  const sn = stock.serialNo ? ` (SN:${stock.serialNo})` : "";
  return `${label}${sn} - ₹${fmtCurr(charger.chargerAmount)}`;
};

const buildLabel = (filters) => {
  if (filters.fromDate && filters.toDate)
    return `${filters.fromDate}_to_${filters.toDate}`;
  if (filters.month && filters.year)
    return `${new Date(0, filters.month - 1).toLocaleString("default", { month: "short" })}_${filters.year}`;
  if (filters.year)  return `Year_${filters.year}`;
  if (filters.month) return new Date(0, filters.month - 1).toLocaleString("default", { month: "long" });
  if (filters.location) return filters.location;
  return "All";
};

// ─── COLUMN DEFINITIONS ────────────────────────────────────────────────────────
const COLUMNS = [
  // Identifiers
  { header: "#",                    key: (b, i) => i + 1 },
  { header: "Invoice No",           key: (b)    => safe(b.invoiceNo) },
  { header: "Invoice Serial",       key: (b)    => fmt(b.invoiceSerial) },
  { header: "Sale Date",            key: (b)    => fmtDate(b.saleDate) },
  { header: "Location",             key: (b)    => safe(b.location) },
  // Vehicle
  { header: "Vehicle Type",         key: (b)    => safe(b.vehicleType) },
  { header: "Company Name",         key: (b)    => safe(b.companyName) },
  { header: "Model",                key: (b)    => safe(b.model) },
  { header: "Engine Number",        key: (b)    => safe(b.engineNumber) },
  { header: "Serial Number",        key: (b)    => safe(b.chasisNumber) },
  { header: "Number Plate",         key: (b)    => safe(b.noPlate) },
  // Customer
  { header: "Customer Name",        key: (b)    => safe(b.customerName) },
  { header: "Father / C/O",         key: (b)    => safe(b.fatherName) },
  { header: "Mobile",               key: (b)    => fmtList(b.mobile) },
  { header: "Address",              key: (b)    => safe(b.address) },
  { header: "Village",              key: (b)    => safe(b.village) },
  { header: "Post Office",          key: (b)    => safe(b.postOffice) },
  { header: "Police Station",       key: (b)    => safe(b.policeStation) },
  { header: "PIN Code",             key: (b)    => safe(b.pinCode) },
  { header: "Landmark",             key: (b)    => safe(b.landmark) },
  { header: "GSTIN",                key: (b)    => safe(b.GSTIN) },
  // Sale Financials
  { header: "ExShowroom Price (₹)", key: (b)    => fmtCurr(b.salePrice) },
  { header: "ORP (₹)",              key: (b)    => fmtCurr(b.orp) },
  { header: "Discount (₹)",         key: (b)    => fmtCurr(b.discount) },
  { header: "Coupon Cashback (₹)",  key: (b)    => fmtCurr(b.couponCashback) },
  { header: "Net Amount (₹)",       key: (b)    => fmtCurr((b.salePrice || 0) - (b.discount || 0)) },
  { header: "Cash Amount (₹)",      key: (b)    => fmtCurr(b.cashAmount) },
  { header: "Sale Type",            key: (b)    => safe(b.saleType) },
  // Finance
  { header: "Finance Company",      key: (b)    => safe(b.finance?.companyName) },
  { header: "Finance Amount (₹)",   key: (b)    => fmtCurr(b.finance?.amount) },
  { header: "Finance Months",       key: (b)    => fmt(b.finance?.months) },
  { header: "Finance EMI (₹)",     key: (b)    => fmtCurr(b.finance?.emiAmount) },
  { header: "Finance Date",        key: (b)    => fmtDate(b.finance?.date) },
  // Exchange
  { header: "Exchange Owner",       key: (b)    => safe(b.exchange?.ownerName) },
  { header: "Exchange Phone",       key: (b)    => safe(b.exchange?.phone) },
  { header: "Exchange Location",    key: (b)    => safe(b.exchange?.location) },
  { header: "Exchange Model",       key: (b)    => safe(b.exchange?.model) },
  { header: "Exchange Reg No",      key: (b)    => safe(b.exchange?.registrationNumber) },
  { header: "Exchange Price (₹)",   key: (b)    => fmtCurr(b.exchange?.exchangePrice) },
  { header: "Exchange Paid (₹)",    key: (b)    => fmtCurr(b.exchange?.paidAmount) },
  { header: "Exchange Remaining (₹)", key: (b)  => fmtCurr(b.exchange?.remainingAmount) },
  { header: "Exchange Status",      key: (b)    => safe(b.exchange?.status) },
  // Due / Installments
  { header: "Due Status",           key: (b)    => b.due?.status || "PAID" },
  { header: "Total Due (₹)",        key: (b)    => fmtCurr(b.due?.totalAmount) },
  { header: "Paid So Far (₹)",      key: (b)    => fmtCurr(b.due?.paidAmount) },
  { header: "Remaining Due (₹)",    key: (b)    => fmtCurr(b.due?.remainingAmount) },
  // Charges
  { header: "RTO (₹)",              key: (b)    => fmtCurr(b.charges?.rto) },
  { header: "Insurance (₹)",        key: (b)    => fmtCurr(b.charges?.insurance) },
  // Expenses
  { header: "Helmet (₹)",           key: (b)    => fmt(b.expenses?.helmet) },
  { header: "Seat Cover (₹)",       key: (b)    => fmt(b.expenses?.seatCover) },
  { header: "Bike Cover (₹)",       key: (b)    => fmt(b.expenses?.bikeCover) },
  { header: "Transport (₹)",        key: (b)    => fmt(b.expenses?.transport) },
  { header: "TCR (₹)",              key: (b)    => fmt(b.expenses?.tcr) },
  { header: "Reg Broker (₹)",       key: (b)    => fmt(b.expenses?.regBroker) },
  // Bike-only Tax (as stored on the sale document — kept for reference)
  { header: "GST Rate (%)",         key: (b)    => fmt(b.tax?.gstRate) },
  { header: "CGST (%)",             key: (b)    => fmt(b.tax?.cgst) },
  { header: "SGST (%)",             key: (b)    => fmt(b.tax?.sgst) },
  { header: "Taxable Amount (₹)",   key: (b)    => fmtCurr(b.tax?.taxableAmount) },
  { header: "Total GST (₹)",        key: (b)    => fmtCurr(b.tax?.totalGstAmount) },
  // Gifts
  { header: "Gifts",                key: (b)    => (b.gifts || []).map((g) => `${g.name} x${g.quantity}`).join("; ") },

  // ✅ 3-phase GST breakdown per component — Battery, then Charger, then Bike-only —
  // each always calculated at the flat 5% default (see DEFAULT_*_GST above).
  { header: "Batteries Sold",              key: (b) => fmtBatteryList(b.battery) },
  { header: "Battery Taxable (₹)",         key: (b) => batteryGst(b).taxable },
  { header: "Battery GST Rate (%)",        key: (b) => batteryGst(b).rate },
  { header: "Battery CGST (₹)",            key: (b) => batteryGst(b).cgst },
  { header: "Battery SGST (₹)",            key: (b) => batteryGst(b).sgst },
  { header: "Battery Total Payable (₹)",   key: (b) => batteryGst(b).total },

  { header: "Charger Sold",                key: (b) => fmtCharger(b.charger) },
  { header: "Charger Taxable (₹)",         key: (b) => chargerGst(b).taxable },
  { header: "Charger GST Rate (%)",        key: (b) => chargerGst(b).rate },
  { header: "Charger CGST (₹)",            key: (b) => chargerGst(b).cgst },
  { header: "Charger SGST (₹)",            key: (b) => chargerGst(b).sgst },
  { header: "Charger Total Payable (₹)",   key: (b) => chargerGst(b).total },

  { header: "Bike Only Amount excl. Battery+Charger (₹)", key: (b) => bikeOnlyAmount(b) },
  { header: "Bike Taxable (₹)",            key: (b) => bikeGst(b).taxable },
  { header: "Bike GST Rate (%)",           key: (b) => bikeGst(b).rate },
  { header: "Bike CGST (₹)",               key: (b) => bikeGst(b).cgst },
  { header: "Bike SGST (₹)",               key: (b) => bikeGst(b).sgst },
  { header: "Bike Total Payable (₹)",      key: (b) => bikeGst(b).total },

  { header: "Total GST Payable (Bike+Battery+Charger) (₹)", key: (b) => totalGstPayable(b) },
  { header: "Total ORP (₹)",               key: (b) => fmtCurr(b.orp) }, // same as invoice's pricing.orp

  // Sold By
  { header: "Sold By Name",         key: (b)    => safe(b.soldBy?.name) },
  { header: "Sold By Role",         key: (b)    => safe(b.soldBy?.role) },
  { header: "Sold By Email",        key: (b)    => safe(b.soldBy?.email) },
  // Remarks
  { header: "Remarks",              key: (b)    => safe(b.remarks) },
];

// ─── STYLES ────────────────────────────────────────────────────────────────────
const HEADER_STYLE = {
  font:      { bold: true, color: { rgb: "FFFFFF" }, name: "Arial", sz: 9 },
  fill:      { fgColor: { rgb: "1565C0" }, patternType: "solid" },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: {
    top:    { style: "thin", color: { rgb: "AAAAAA" } },
    bottom: { style: "thin", color: { rgb: "AAAAAA" } },
    left:   { style: "thin", color: { rgb: "AAAAAA" } },
    right:  { style: "thin", color: { rgb: "AAAAAA" } },
  },
};

const ERICKSHAW_HEADER = { ...HEADER_STYLE, fill: { fgColor: { rgb: "1B5E20" }, patternType: "solid" } };
const SCOOTY_HEADER    = { ...HEADER_STYLE, fill: { fgColor: { rgb: "4A148C" }, patternType: "solid" } };
const DUE_HEADER       = { ...HEADER_STYLE, fill: { fgColor: { rgb: "E65100" }, patternType: "solid" } };
const EXCHANGE_HEADER  = { ...HEADER_STYLE, fill: { fgColor: { rgb: "880E4F" }, patternType: "solid" } };
const BATTERY_HEADER   = { ...HEADER_STYLE, fill: { fgColor: { rgb: "00695C" }, patternType: "solid" } };

const cellStyle = (isDue) => ({
  font:      { name: "Arial", sz: 9 },
  fill:      { fgColor: { rgb: isDue ? "FFF8F5" : "FFFFFF" }, patternType: "solid" },
  alignment: { vertical: "center" },
  border: {
    top:    { style: "hair", color: { rgb: "E0E0E0" } },
    bottom: { style: "hair", color: { rgb: "E0E0E0" } },
    left:   { style: "hair", color: { rgb: "E0E0E0" } },
    right:  { style: "hair", color: { rgb: "E0E0E0" } },
  },
});

const numStyle = (isDue) => ({
  ...cellStyle(isDue),
  numFmt: '₹#,##0;(₹#,##0);"-"',
  alignment: { horizontal: "right", vertical: "center" },
});

const isCurrencyHeader = (h) => typeof h === "string" && h.includes("(₹)");

// ─── HELPER: apply styles to a sheet ──────────────────────────────────────────
const applySheetStyles = (ws, headers, rows, data, headerStyle) => {
  headers.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
    if (!ws[ref]) ws[ref] = { t: "s", v: headers[ci] };
    ws[ref].s = headerStyle;
  });
  data.forEach((b, ri) => {
    const isDue = b?.due?.status === "DUE";
    headers.forEach((h, ci) => {
      const ref = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
      if (ws[ref]) {
        const val = rows[ri][ci];
        const isCurr = typeof val === "number" && isCurrencyHeader(h);
        ws[ref].s = isCurr ? numStyle(isDue) : cellStyle(isDue);
      }
    });
  });
  ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length + 2, 14) }));
  ws["!rows"] = [{ hpt: 28 }];
  ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomLeft" };
};

// ─── MAIN EXCEL GENERATOR ──────────────────────────────────────────────────────
const generateExcel = (data, filters) => {
  const wb    = XLSX.utils.book_new();
  const label = buildLabel(filters);

  // ── Sheet 1: ALL RECORDS ────────────────────────────────────────────────
  const allRows = data.map((b, i) => COLUMNS.map((c) => c.key(b, i)));
  const allWs   = XLSX.utils.aoa_to_sheet([COLUMNS.map((c) => c.header), ...allRows]);
  applySheetStyles(allWs, COLUMNS.map((c) => c.header), allRows, data, HEADER_STYLE);
  XLSX.utils.book_append_sheet(wb, allWs, "All Records");

  // ── Sheet 2: E-RICKSHAW only ───────────────────────────────────────────────
  const eRick     = data.filter((b) => b.vehicleType === "E_RICKSHAW");
  const eRickRows = eRick.map((b, i) => COLUMNS.map((c) => c.key(b, i)));
  const eRickWs   = XLSX.utils.aoa_to_sheet([COLUMNS.map((c) => c.header), ...eRickRows]);
  applySheetStyles(eRickWs, COLUMNS.map((c) => c.header), eRickRows, eRick, ERICKSHAW_HEADER);
  XLSX.utils.book_append_sheet(wb, eRickWs, "E-Rickshaw");

  // ── Sheet 3: SCOOTY only ──────────────────────────────────────────────────
  const scooty     = data.filter((b) => b.vehicleType === "SCOOTY");
  const scootyRows = scooty.map((b, i) => COLUMNS.map((c) => c.key(b, i)));
  const scootyWs   = XLSX.utils.aoa_to_sheet([COLUMNS.map((c) => c.header), ...scootyRows]);
  applySheetStyles(scootyWs, COLUMNS.map((c) => c.header), scootyRows, scooty, SCOOTY_HEADER);
  XLSX.utils.book_append_sheet(wb, scootyWs, "Scooty");

  // ── Sheet 4: LOCATION SUMMARY ─────────────────────────────────────────────
  const locations = [...new Set(data.map((b) => b.location || "Other"))];
  const sumHeaders = [
    "Location", "Vehicle Type", "Total", "CASH", "FINANCE", "EXCHANGE", "EX+FIN", "CASH+EX",
    "PAID", "DUE",
    "ExShowroom (₹)", "ORP (₹)", "Finance Amt (₹)",
    "Due Total (₹)", "Due Remaining (₹)",
    "Exchange Due (₹)"
  ];

  const sumRows = [];
  locations.forEach((loc) => {
    ["ALL", "E_RICKSHAW", "SCOOTY"].forEach((vt) => {
      const bikes = data.filter((b) =>
        (b.location || "Other") === loc &&
        (vt === "ALL" || b.vehicleType === vt)
      );
      if (!bikes.length) return;
      sumRows.push([
        loc,
        vt === "ALL" ? "ALL" : vt,
        bikes.length,
        bikes.filter((b) => b.saleType === "CASH").length,
        bikes.filter((b) => b.saleType === "FINANCE").length,
        bikes.filter((b) => b.saleType === "EXCHANGE").length,
        bikes.filter((b) => b.saleType === "EX+FIN").length,
        bikes.filter((b) => b.saleType === "CASH+EX").length,
        bikes.filter((b) => b.due?.status !== "DUE").length,
        bikes.filter((b) => b.due?.status === "DUE").length,
        bikes.reduce((s, b) => s + (b.salePrice || 0), 0),
        bikes.reduce((s, b) => s + (b.orp || 0), 0),
        bikes.reduce((s, b) => s + (b.finance?.amount || 0), 0),
        bikes.reduce((s, b) => s + (b.due?.totalAmount || 0), 0),
        bikes.reduce((s, b) => s + (b.due?.remainingAmount || 0), 0),
        bikes.reduce((s, b) => s + (b.exchange?.remainingAmount || 0), 0),
      ]);
    });
  });

  sumRows.push([
    "GRAND TOTAL", "ALL",
    data.length,
    data.filter((b) => b.saleType === "CASH").length,
    data.filter((b) => b.saleType === "FINANCE").length,
    data.filter((b) => b.saleType === "EXCHANGE").length,
    data.filter((b) => b.saleType === "EX+FIN").length,
    data.filter((b) => b.saleType === "CASH+EX").length,
    data.filter((b) => b.due?.status !== "DUE").length,
    data.filter((b) => b.due?.status === "DUE").length,
    data.reduce((s, b) => s + (b.salePrice || 0), 0),
    data.reduce((s, b) => s + (b.orp || 0), 0),
    data.reduce((s, b) => s + (b.finance?.amount || 0), 0),
    data.reduce((s, b) => s + (b.due?.totalAmount || 0), 0),
    data.reduce((s, b) => s + (b.due?.remainingAmount || 0), 0),
    data.reduce((s, b) => s + (b.exchange?.remainingAmount || 0), 0),
  ]);

  const sumWs = XLSX.utils.aoa_to_sheet([sumHeaders, ...sumRows]);
  sumHeaders.forEach((h, ci) => {
    const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
    if (!sumWs[ref]) sumWs[ref] = { t: "s", v: h };
    sumWs[ref].s = HEADER_STYLE;
  });
  const totRow = sumRows.length;
  sumHeaders.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: totRow, c: ci });
    if (sumWs[ref]) {
      sumWs[ref].s = {
        font: { bold: true, name: "Arial", sz: 9 },
        fill: { fgColor: { rgb: "E3F2FD" }, patternType: "solid" },
        alignment: { horizontal: ci > 1 ? "right" : "left", vertical: "center" },
        border: { top: { style: "medium", color: { rgb: "1565C0" } } },
      };
    }
  });
  sumWs["!cols"] = sumHeaders.map(() => ({ wch: 22 }));
  XLSX.utils.book_append_sheet(wb, sumWs, "Location Summary");

  // ── Sheet 5: DUE LIST ─────────────────────────────────────────────────────
  const dueData = data.filter((b) => b?.due?.status === "DUE");
  if (dueData.length > 0) {
    const dueHeaders = [
      "#", "Invoice No", "Sale Date", "Location", "Vehicle Type",
      "Company", "Model", "Engine No", "Chasis No",
      "Customer", "Mobile", "Address",
      "ExShowroom (₹)", "Sale Type",
      "Total Due (₹)", "Paid (₹)", "Remaining (₹)"
    ];
    const dueRows = dueData.map((b, i) => [
      i + 1, safe(b.invoiceNo), fmtDate(b.saleDate), safe(b.location), safe(b.vehicleType),
      safe(b.companyName), safe(b.model), safe(b.engineNumber), safe(b.chasisNumber),
      safe(b.customerName), fmtList(b.mobile), safe(b.address),
      fmtCurr(b.salePrice), safe(b.saleType),
      fmtCurr(b.due?.totalAmount), fmtCurr(b.due?.paidAmount), fmtCurr(b.due?.remainingAmount),
    ]);
    const dueWs = XLSX.utils.aoa_to_sheet([dueHeaders, ...dueRows]);
    dueHeaders.forEach((h, ci) => {
      const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!dueWs[ref]) dueWs[ref] = { t: "s", v: h };
      dueWs[ref].s = DUE_HEADER;
    });
    dueRows.forEach((_, ri) => {
      dueHeaders.forEach((_, ci) => {
        const ref = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (dueWs[ref]) dueWs[ref].s = cellStyle(true);
      });
    });
    dueWs["!cols"] = dueHeaders.map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, dueWs, "Due Payments");
  }

  // ── Sheet 6: EXCHANGE DUE LIST ────────────────────────────────────────────
  const exchDue = data.filter((b) => b?.exchange?.status === "DUE");
  if (exchDue.length > 0) {
    const exHeaders = [
      "#", "Invoice No", "Sale Date", "Location", "Customer",
      "Exchange Owner", "Exchange Phone", "Exchange Model",
      "Reg No", "Exchange Price (₹)", "Paid (₹)", "Remaining (₹)"
    ];
    const exRows = exchDue.map((b, i) => [
      i + 1, safe(b.invoiceNo), fmtDate(b.saleDate), safe(b.location), safe(b.customerName),
      safe(b.exchange?.ownerName), safe(b.exchange?.phone), safe(b.exchange?.model),
      safe(b.exchange?.registrationNumber),
      fmtCurr(b.exchange?.exchangePrice), fmtCurr(b.exchange?.paidAmount),
      fmtCurr(b.exchange?.remainingAmount),
    ]);
    const exWs = XLSX.utils.aoa_to_sheet([exHeaders, ...exRows]);
    exHeaders.forEach((h, ci) => {
      const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!exWs[ref]) exWs[ref] = { t: "s", v: h };
      exWs[ref].s = EXCHANGE_HEADER;
    });
    exRows.forEach((_, ri) => {
      exHeaders.forEach((_, ci) => {
        const ref = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (exWs[ref]) exWs[ref].s = cellStyle(false);
      });
    });
    exWs["!cols"] = exHeaders.map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, exWs, "Exchange Due");
  }

  // ── Sheet 7: BATTERY & CHARGER SOLD (one row per unit) ─────────────────────
  // ✅ Always uses the flat 5% default rate — no backend rate lookup.
  const bcRows = [];
  data.forEach((b) => {
    (b.battery || []).forEach((bt) => {
      const stock = bt.batteryStock || {};
      const rate = DEFAULT_BATTERY_GST;
      const g = gstBreakdown(bt.batteryAmount, rate);
      bcRows.push([
        safe(b.invoiceNo),
        fmtDate(b.saleDate),
        safe(b.customerName),
        safe(b.vehicleType),
        safe(b.model),
        "BATTERY",
        safe(stock.companyName),
        safe(stock.model),
        safe(stock.serialNo),
        safe(stock.batteryType),
        fmtCurr(bt.batteryAmount),
        g.taxable,
        g.rate,
        g.cgst,
        g.sgst,
        g.total,
      ]);
    });
    if (b.charger) {
      const stock = b.charger.chargerStock || {};
      const rate = DEFAULT_CHARGER_GST;
      const g = gstBreakdown(b.charger.chargerAmount, rate);
      bcRows.push([
        safe(b.invoiceNo),
        fmtDate(b.saleDate),
        safe(b.customerName),
        safe(b.vehicleType),
        safe(b.model),
        "CHARGER",
        safe(stock.companyName),
        safe(stock.model),
        safe(stock.serialNo),
        "",
        fmtCurr(b.charger.chargerAmount),
        g.taxable,
        g.rate,
        g.cgst,
        g.sgst,
        g.total,
      ]);
    }
  });

  if (bcRows.length > 0) {
    const bcHeaders = [
      "Bike Invoice No", "Sale Date", "Customer", "Bike Vehicle Type", "Bike Model",
      "Item Type", "Company Name", "Item Model", "Serial No", "Battery Type",
      "Amount (₹)", "Taxable (₹)", "GST Rate (%)", "CGST (₹)", "SGST (₹)", "Total Payable (₹)"
    ];
    const bcWs = XLSX.utils.aoa_to_sheet([bcHeaders, ...bcRows]);
    bcHeaders.forEach((h, ci) => {
      const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!bcWs[ref]) bcWs[ref] = { t: "s", v: h };
      bcWs[ref].s = BATTERY_HEADER;
    });
    bcRows.forEach((_, ri) => {
      bcHeaders.forEach((h, ci) => {
        const ref = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (bcWs[ref]) {
          const val = bcRows[ri][ci];
          bcWs[ref].s = typeof val === "number" && isCurrencyHeader(h) ? numStyle(false) : cellStyle(false);
        }
      });
    });
    bcWs["!cols"] = bcHeaders.map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, bcWs, "Battery & Charger Sold");
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  const filename = `EV_Sales_${label}_${new Date().toISOString().split("T")[0]}.xlsx`;
  XLSX.writeFile(wb, filename, { bookType: "xlsx", type: "binary", cellStyles: true });
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────
const ExportexcelSell = ({ data = [], filters = {} }) => {
  const [loading, setLoading] = useState(false);

  const handleExport = () => {
    if (!data.length) return;
    setLoading(true);
    setTimeout(() => {
      try {
        generateExcel(data, filters);
      } finally {
        setLoading(false);
      }
    }, 100);
  };

  return (
    <Tooltip title={`Export ${data.length} EV records to Excel`} arrow>
      <span>
        <Button
          variant="outlined"
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <FileDownloadIcon />}
          onClick={handleExport}
          disabled={loading || !data.length}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            fontSize: "0.75rem",
            color: "#2e7d32",
            borderColor: "#2e7d32",
            "&:hover": { bgcolor: "#e8f5e9", borderColor: "#1b5e20" },
          }}
        >
          {loading ? "Generating…" : "Export Excel"}
        </Button>
      </span>
    </Tooltip>
  );
};

export default ExportexcelSell;